// Which churches a state admits, and what to relax when it admits none.
// Pure functions over the church records the endpoint returns.

import { massesOn, massSoon } from './schedule.js';
import { haversineMiles } from './geo.js';

const KIND_WORDS = { c: 'cathedral', b: 'basilica', s: 'shrine', m: 'mission', p: 'parish' };

/** The place filter is 'city:Name', 'nb:slug' or 'region:Name'. */
export function parsePlace(place) {
  const i = (place || '').indexOf(':');
  if (i < 1) return null;
  return { type: place.slice(0, i), value: place.slice(i + 1) };
}

function matchesPlace(c, p) {
  if (!p) return true;
  if (p.type === 'city') return (c.c || '').toLowerCase() === p.value.toLowerCase();
  if (p.type === 'nb') return !!(c.nb && c.nb.includes(p.value));
  if (p.type === 'region') return (c.r || '').toLowerCase() === p.value.toLowerCase();
  if (p.type === 'deanery') return String(c.d) === p.value;
  if (p.type === 'state') return (c.st || '').toLowerCase() === p.value.toLowerCase();
  return true;
}

/**
 * Builds one predicate per active constraint so the empty state can ask
 * "which single constraint, removed, gives results?".
 * `ctx` carries clock, languageIndex and the search matcher.
 */
export function constraints(s, ctx) {
  const out = [];
  if (s.q && ctx.textMatch) out.push({ key: 'q', test: (c) => ctx.textMatch(c, s.q) });
  const p = parsePlace(s.place);
  if (p) out.push({ key: 'place', test: (c) => matchesPlace(c, p) });
  if (s.near) out.push({ key: 'near', test: (c) => haversineMiles(s.near[0], s.near[1], c.lat, c.lng) <= s.near[2] });
  if (s.lang) {
    const li = ctx.languageIndex ? ctx.languageIndex.indexOf(s.lang) : -1;
    out.push({ key: 'lang', test: (c) => {
      if (c.l && c.l.includes(s.lang)) return true;
      return li >= 0 && !!c.ms && c.ms.some((r) => r[2] === li);
    } });
  }
  if (s.day >= 0 || s.after >= 0) {
    // With a day and no hour: any Mass that day. With an hour and no day: today after the hour.
    const day = s.day >= 0 ? s.day : ctx.clock.day;
    const li = s.lang && ctx.languageIndex ? ctx.languageIndex.indexOf(s.lang) : -1;
    out.push({ key: s.day >= 0 ? 'day' : 'after', test: (c) => massesOn(c.ms, s.day >= 0 ? s.day : (ctx.clockFor ? ctx.clockFor(c).day : day), s.after).some((r) => li < 0 || r[2] === li) });
  }
  if (s.now) {
    const li = s.lang && ctx.languageIndex ? ctx.languageIndex.indexOf(s.lang) : -1;
    out.push({ key: 'now', test: (c) => massSoon(c.ms, ctx.clockFor ? ctx.clockFor(c) : ctx.clock, 120, li >= 0 ? li : '') });
  }
  if (s.hc) out.push({ key: 'hc', test: (c) => !!c.hc });
  if (s.ha) out.push({ key: 'ha', test: (c) => !!c.ha });
  if (s.hp) out.push({ key: 'hp', test: (c) => !!c.hp });
  if (s.lm) out.push({ key: 'lm', test: (c) => !!c.lm });
  if (s.sc) out.push({ key: 'sc', test: (c) => !!c.sc });
  if (s.kind) out.push({ key: 'kind', test: (c) => c.k === s.kind });
  return out;
}

export function applyFilters(churches, s, ctx) {
  const cs = constraints(s, ctx);
  if (!cs.length) return churches.slice();
  return churches.filter((c) => cs.every((k) => k.test(c)));
}

/**
 * For an empty result: every single constraint whose removal yields matches,
 * with the count, plus a wider radius when a radius is set. Sorted by the
 * number of churches each relaxation would give back.
 */
export function relaxations(churches, s, ctx) {
  const cs = constraints(s, ctx);
  const out = [];
  for (const drop of cs) {
    const rest = cs.filter((k) => k !== drop);
    const n = churches.reduce((acc, c) => acc + (rest.every((k) => k.test(c)) ? 1 : 0), 0);
    if (n > 0) out.push({ key: drop.key, count: n });
  }
  if (s.near) {
    for (const mult of [2, 4]) {
      const wider = { ...s, near: [s.near[0], s.near[1], Math.round(s.near[2] * mult)] };
      const n = applyFilters(churches, wider, ctx).length;
      if (n > 0) { out.push({ key: 'radius', miles: wider.near[2], count: n }); break; }
    }
  }
  return out.sort((a, b) => b.count - a.count);
}

/** Counts per facet over a set, for the sidebar's facet labels. */
export function facetCounts(churches, languageIndex) {
  const langs = new Map();
  const kinds = { p: 0, c: 0, b: 0, s: 0, m: 0 };
  let hc = 0, ha = 0, hp = 0, lm = 0, sc = 0;
  for (const c of churches) {
    kinds[c.k] = (kinds[c.k] || 0) + 1;
    if (c.hc) hc++; if (c.ha) ha++; if (c.hp) hp++; if (c.lm) lm++; if (c.sc) sc++;
    const seen = new Set(c.l || []);
    if (c.ms && languageIndex) for (const r of c.ms) seen.add(languageIndex[r[2]]);
    for (const code of seen) if (code) langs.set(code, (langs.get(code) || 0) + 1);
  }
  return { langs: [...langs.entries()].sort((a, b) => b[1] - a[1]), kinds, hc, ha, hp, lm, sc };
}

export function kindWord(k) { return KIND_WORDS[k] || 'parish'; }
